import React, { useState, useEffect, useCallback } from 'react';
import { format, subDays } from 'date-fns';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from 'chart.js';
import { Line } from 'react-chartjs-2';
import { getDailyTrends, getBidderTrends } from '../services/api';
import DateRangePicker from '../components/DateRangePicker';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend, Filler);

const COLORS = ['#667eea', '#00b894', '#e17055', '#fdcb6e', '#0984e3', '#6c5ce7', '#00cec9'];

function TrendsPage() {
  const [dailyData, setDailyData] = useState([]);
  const [bidderData, setBidderData] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [dateRange, setDateRange] = useState({
    from: format(subDays(new Date(), 30), 'yyyy-MM-dd'),
    to: format(new Date(), 'yyyy-MM-dd'),
  });
  const [selectedBidders, setSelectedBidders] = useState([]);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [dailyRes, bidderRes] = await Promise.all([
        getDailyTrends(dateRange),
        getBidderTrends(dateRange),
      ]);
      setDailyData(dailyRes.data.summaries || []);
      setBidderData(bidderRes.data.bidderTrends || {});
      if (selectedBidders.length === 0) {
        setSelectedBidders(Object.keys(bidderRes.data.bidderTrends || {}).slice(0, 5));
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load trends');
    } finally {
      setLoading(false);
    }
  }, [dateRange]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { loadData(); }, [loadData]);

  const labels = dailyData.map((d) => d.date);

  const makeChartData = (label, key, color) => ({
    labels,
    datasets: [{
      label,
      data: dailyData.map((d) => d[key]),
      borderColor: color,
      backgroundColor: color + '20',
      fill: true,
      tension: 0.3,
    }],
  });

  const lineOptions = { responsive: true, plugins: { legend: { display: false } }, scales: { y: { beginAtZero: true } } };

  const bidderChartData = {
    labels: dailyData.map((d) => d.date),
    datasets: selectedBidders.map((bidder, i) => ({
      label: bidder,
      data: dailyData.map((d) => {
        const b = (bidderData[bidder] || []).find((x) => x.date === d.date);
        return b?.avgCpm || 0;
      }),
      borderColor: COLORS[i % COLORS.length],
      tension: 0.3,
    })),
  };

  if (loading) return <div className="loading">Loading trends...</div>;
  if (error) return <div className="error-msg">{error}</div>;

  if (dailyData.length === 0) {
    return (
      <div>
        <div className="section-header">
          <h2>Trends</h2>
          <DateRangePicker from={dateRange.from} to={dateRange.to} onChange={setDateRange} />
        </div>
        <div className="empty-state">
          <h3>No Trend Data</h3>
          <p>Trends will appear once daily summaries are generated from collected logs.</p>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="section-header">
        <h2>Trends</h2>
        <DateRangePicker from={dateRange.from} to={dateRange.to} onChange={setDateRange} />
      </div>

      <div className="charts-row">
        <div className="chart-card">
          <h3>Estimated Revenue ($)</h3>
          <Line data={makeChartData('Revenue', 'estRevenue', '#00b894')} options={lineOptions} />
        </div>
        <div className="chart-card">
          <h3>Auction Volume</h3>
          <Line data={makeChartData('Auctions', 'totalAuctions', '#667eea')} options={lineOptions} />
        </div>
      </div>

      <div className="charts-row">
        <div className="chart-card">
          <h3>Timeout Rate</h3>
          <Line data={makeChartData('Timeout Rate', 'timeoutRate', '#e17055')} options={lineOptions} />
        </div>
        <div className="chart-card">
          <h3>No-Bid Rate</h3>
          <Line data={makeChartData('No-Bid Rate', 'noBidRate', '#fdcb6e')} options={lineOptions} />
        </div>
      </div>

      <div className="charts-row">
        <div className="chart-card">
          <h3>Average CPM ($)</h3>
          <Line data={makeChartData('Avg CPM', 'avgCpm', '#0984e3')} options={lineOptions} />
        </div>
        <div className="chart-card">
          <h3>Average Latency (ms)</h3>
          <Line data={makeChartData('Avg Latency', 'avgLatency', '#6c5ce7')} options={lineOptions} />
        </div>
      </div>

      {Object.keys(bidderData).length > 0 && (
        <>
          <div className="section-header" style={{ marginTop: 24 }}>
            <h3>Per-Bidder CPM Trends</h3>
            <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
              {Object.keys(bidderData).map((bidder) => (
                <button
                  key={bidder}
                  className={`btn btn-sm ${selectedBidders.includes(bidder) ? 'btn-primary' : 'btn-secondary'}`}
                  onClick={() => {
                    setSelectedBidders((prev) =>
                      prev.includes(bidder) ? prev.filter((b) => b !== bidder) : [...prev, bidder]
                    );
                  }}
                >
                  {bidder}
                </button>
              ))}
            </div>
          </div>
          <div className="chart-card">
            <Line data={bidderChartData} options={{ responsive: true, tension: 0.3 }} />
          </div>
        </>
      )}
    </div>
  );
}

export default TrendsPage;
